import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import SEO from "../../components/SEO";
import Header from "../../components/ui/Header";
import { PRODUCT_CATEGORIES, CATEGORY_TO_ID, allProducts as importedProducts } from "../../constants/productsData";

import Button from "../../components/ui/Button";
import LeadFormButton from "../../components/LeadFormButton";
import FilterSidebar from "./components/FilterSidebar";
import ProductGrid from "./components/ProductGrid";
import QuickViewModal from "./components/QuickViewModal";
import ComparisonPanel from "./components/ComparisonPanel";
import WishlistPanel from "./components/WishlistPanel";

const InteractiveCatalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Ler parâmetro de categoria da URL
  const categoryParam = searchParams.get('category');
  const initialCategoryId = categoryParam && CATEGORY_TO_ID[categoryParam] !== undefined 
    ? [CATEGORY_TO_ID[categoryParam]] 
    : [];

  const [filters, setFilters] = useState({
    categories: initialCategoryId,
    priceRange: { min: null, max: null },
    searchTerm: '',
  });

  const [sortBy, setSortBy] = useState("relevance");
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [comparisonItems, setComparisonItems] = useState([]);
  const [loading, setLoading] = useState(false);
  console.log(filters);
  const productsPerPage = 12;

  // Use products from constants
  const allProducts = importedProducts;

  // Atualizar filtro quando o parâmetro de categoria mudar na URL
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && CATEGORY_TO_ID[categoryParam] !== undefined) {
      const categoryId = CATEGORY_TO_ID[categoryParam];
      setFilters(prev => ({
        ...prev,
        categories: [categoryId]
      }));
    }
  }, [searchParams]);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...allProducts];

    // Filtro por nome do produto
    if (filters?.searchTerm?.trim()) {
      const searchLower = filters.searchTerm.toLowerCase().trim();
      filtered = filtered.filter((product) =>
        product?.name?.toLowerCase().includes(searchLower) ||
        product?.description?.toLowerCase().includes(searchLower)
      );
    }

    // Filtro por categorias
    if (filters?.categories?.length > 0) {
      filtered = filtered.filter((product) =>
        filters.categories.includes(product?.categoryId)
      );
    }

    // Filtro por faixa de preço
    if (
      filters?.priceRange?.min !== null ||
      filters?.priceRange?.max !== null
    ) {
      filtered = filtered.filter((product) => {
        const price = product?.price || product?.priceRange?.min || 0;
        const minCheck =
          filters?.priceRange?.min === null ||
          price >= filters?.priceRange?.min;
        const maxCheck =
          filters?.priceRange?.max === null ||
          price <= filters?.priceRange?.max;
        return minCheck && maxCheck;
      });
    }


    // Apply sorting
    switch (sortBy) {
      case "price-low":
        filtered?.sort(
          (a, b) =>
            (a?.price || a?.priceRange?.min || 0) -
            (b?.price || b?.priceRange?.min || 0)
        );
        break;
      case "price-high":
        filtered?.sort(
          (a, b) =>
            (b?.price || b?.priceRange?.max || 0) -
            (a?.price || a?.priceRange?.max || 0)
        );
        break;
      case "newest":
        filtered?.sort((a, b) => (b?.isNew ? 1 : 0) - (a?.isNew ? 1 : 0));
        break;
      case "popular":
        filtered?.sort((a, b) => b?.id - a?.id);
        break;
      default:
        // Keep original order for relevance
        break;
    }

    return filtered;
  }, [filters, sortBy]);

  // Pagination
  const totalPages = Math.ceil(
    filteredAndSortedProducts?.length / productsPerPage
  );
  const paginatedProducts = filteredAndSortedProducts?.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );

  // Get products for comparison and wishlist
  const comparisonProducts = allProducts?.filter((product) =>
    comparisonItems?.includes(product?.id)
  );
  const wishlistProducts = allProducts?.filter((product) =>
    wishlistItems?.includes(product?.id)
  );

  // Calculate real counts for filters
  const getFilterCounts = () => {
    const counts = {
      categories: {},
    };

    allProducts?.forEach((product) => {
      // Count categories
      if (product?.categoryId !== undefined && PRODUCT_CATEGORIES[product.categoryId]) {
        const categoryValue = PRODUCT_CATEGORIES[product.categoryId];
        counts.categories[categoryValue] = (counts.categories[categoryValue] || 0) + 1;
      }
    });

    return counts;
  };

  // Calcular preço máximo para o slider
  const maxPrice = useMemo(() => {
    return Math.max(
      ...allProducts.map((product) => 
        product?.price || product?.priceRange?.max || product?.priceRange?.min || 0
      ),
      10000 // valor padrão mínimo
    );
  }, [allProducts]);

  const filterCounts = getFilterCounts();

  // Event handlers
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      categories: [],
      priceRange: { min: null, max: null },
      searchTerm: '',
    });
    setCurrentPage(1);
  };

  const handleQuickView = (product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  };

  const handleAddToWishlist = (productId) => {
    setWishlistItems((prev) =>
      prev?.includes(productId)
        ? prev?.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleAddToComparison = (productId) => {
    setComparisonItems((prev) => {
      if (prev?.includes(productId)) {
        return prev?.filter((id) => id !== productId);
      } else if (prev?.length < 3) {
        return [...prev, productId];
      }
      return prev;
    });
  };

  const handleRemoveFromComparison = (productId) => {
    setComparisonItems((prev) => prev?.filter((id) => id !== productId));
  };

  const handleRemoveFromWishlist = (productId) => {
    setWishlistItems((prev) => prev?.filter((id) => id !== productId));
  };

  const handleClearComparison = () => {
    setComparisonItems([]);
  };

  const handleClearWishlist = () => {
    setWishlistItems([]);
  };

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Catálogo Interativo - PH Móveis Artesanais",
    "description": "Explore nossa coleção exclusiva de móveis artesanais únicos em madeiras nobres brasileiras.",
    "url": "https://phmoveis.com.br/interactive-catalog",
    "mainEntity": {
      "@type": "ItemList",
      "name": "Móveis Artesanais",
      "description": "Coleção de móveis únicos em madeiras nobres",
      "numberOfItems": allProducts.length,
      "itemListElement": allProducts.map((product, index) => ({
        "@type": "Product",
        "position": index + 1,
        "name": product.name,
        "description": product.description,
        "image": product.image || product.images?.[0] || "/assets/images/no_image.png",
        "brand": {
          "@type": "Brand",
          "name": "PH Móveis Artesanais"
        },
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": "BRL",
          "availability": "https://schema.org/InStock"
        }
      }))
    }
  };

  return (
    <>
      <SEO
        title="Catálogo Interativo | Móveis Artesanais Únicos"
        description="Explore nossa coleção exclusiva de móveis artesanais únicos em madeiras nobres brasileiras. Filtros avançados, visualização 3D e personalização completa."
        keywords="catálogo móveis artesanais, móveis personalizados, madeira nobre, ipê, cumaru, teca, peroba, móveis sob medida, marcenaria artesanal, design contemporâneo"
        image="https://phmoveis.com.br/assets/catalog-og.jpg"
        url="https://phmoveis.com.br/interactive-catalog"
        type="website"
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 lg:pt-20">
        {/* Hero Section */}
        <div className="bg-gradient-forest text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="font-playfair font-bold text-4xl lg:text-5xl mb-4">
                Catálogo Interativo
              </h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto">
                Explore nossa coleção exclusiva de móveis artesanais. Cada peça
                é única, feita especialmente para você com madeiras nobres
                brasileiras.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex gap-8">
            {/* Filter Sidebar */}
            <FilterSidebar
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              isOpen={isFilterSidebarOpen}
              onToggle={() => setIsFilterSidebarOpen(!isFilterSidebarOpen)}
              filterCounts={filterCounts}
              maxPrice={maxPrice}
            />

            {/* Main Content Area */}
            <div className="flex-1 min-w-0">
              {/* Mobile Filter Toggle & Actions */}
              <div className="lg:hidden flex items-center justify-between mb-6">
                <Button
                  variant="outline"
                  iconName="Filter"
                  iconPosition="left"
                  onClick={() => setIsFilterSidebarOpen(true)}
                >
                  Filtros
                </Button>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Heart"
                    iconPosition="left"
                    onClick={() => setIsWishlistOpen(true)}
                    className={
                      wishlistItems?.length > 0
                        ? "text-red-500 border-red-500"
                        : ""
                    }
                  >
                    {wishlistItems?.length > 0 && (
                      <span className="ml-1">({wishlistItems?.length})</span>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="GitCompare"
                    iconPosition="left"
                    onClick={() => setIsComparisonOpen(true)}
                    disabled={comparisonItems?.length === 0}
                    className={
                      comparisonItems?.length > 0
                        ? "text-primary border-primary"
                        : ""
                    }
                  >
                    {comparisonItems?.length > 0 && (
                      <span className="ml-1">({comparisonItems?.length})</span>
                    )}
                  </Button>
                </div>
              </div>

              {/* Desktop Actions Bar */}
              <div className="hidden lg:flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <h2 className="font-playfair font-semibold text-2xl text-text-primary">
                    Nossos Produtos
                  </h2>
                </div>

                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="GitCompare"
                    iconPosition="left"
                    onClick={() => setIsComparisonOpen(true)}
                    disabled={comparisonItems?.length === 0}
                    className={
                      comparisonItems?.length > 0
                        ? "text-primary border-primary"
                        : ""
                    }
                  >
                    Comparar
                    {comparisonItems?.length > 0 && (
                      <span className="ml-1">({comparisonItems?.length})</span>
                    )}
                  </Button>
                </div>
              </div>

              {/* Product Grid */}
              <ProductGrid
                products={paginatedProducts}
                loading={loading}
                onQuickView={handleQuickView}
                onAddToWishlist={handleAddToWishlist}
                onAddToComparison={handleAddToComparison}
                wishlistItems={wishlistItems}
                comparisonItems={comparisonItems}
                sortBy={sortBy}
                onSortChange={setSortBy}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        </div>

        {/* Consultation CTA */}
        <div className="bg-muted py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-playfair font-bold text-3xl text-text-primary mb-4">
              Não encontrou o que procura?
            </h2>
            <p className="text-lg text-text-secondary mb-8">
              Nossa equipe de artesãos pode criar a peça perfeita para você.
              Entre em contato para uma consultoria personalizada gratuita.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <LeadFormButton
                variant="default"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                className="bg-gradient-forest"
              >
                Consultoria
              </LeadFormButton>
              <LeadFormButton
                variant="outline"
                size="lg"
                iconName="Phone"
                iconPosition="left"
              >
                Agendar Visita
              </LeadFormButton>
            </div>
          </div>
        </div>
      </div>
      {/* Modals */}
      <QuickViewModal
        product={selectedProduct}
        isOpen={isQuickViewOpen}
        onClose={() => {
          setIsQuickViewOpen(false);
          setSelectedProduct(null);
        }}
      />
      <ComparisonPanel
        products={comparisonProducts}
        isOpen={isComparisonOpen}
        onClose={() => setIsComparisonOpen(false)}
        onRemoveProduct={handleRemoveFromComparison}
        onClearAll={handleClearComparison}
      />
      <WishlistPanel
        products={wishlistProducts}
        isOpen={isWishlistOpen}
        onClose={() => setIsWishlistOpen(false)}
        onRemoveProduct={handleRemoveFromWishlist}
        onClearAll={handleClearWishlist}
        onAddToComparison={handleAddToComparison}
        comparisonItems={comparisonItems}
      />
    </div>
    </>
  );
};

export default InteractiveCatalog;
